import React from 'react';
import {
  PHP8_DESIGNATED_PATH_PREFIXES,
  PHP_DEPRECATION_EXTENSIONS,
  UPLOAD_FILE_AND_FOLDER_SPACE_MESSAGE,
  UPLOAD_FILE_SPACE_MESSAGE,
  UPLOAD_FILENAME_SPACE_MESSAGE,
  UPLOAD_FOLDER_SPACE_MESSAGE,
  UPLOAD_PHP_AND_ZIP_RESTRICTION_MESSAGE,
  UPLOAD_PHP_RESTRICTION_MESSAGE,
  UPLOAD_ZIP_CONTENT_PHP_RESTRICTION_MESSAGE,
  UPLOAD_ZIP_CONTENT_SPACE_MESSAGE,
  UPLOAD_ZIP_FILENAME_SPACE_MESSAGE,
  UPLOAD_OVERSIZE_MESSAGE,
  UPLOAD_ZIP_MANIFEST_REQUIRED_MESSAGE,
  UPLOAD_ZIP_UNSAFE_MESSAGE,
} from '../../../Constants';
import { getUploadableFiles, normalizeConflictDisplayName } from './bulkUploadConflictUtils';

export {
  UPLOAD_FILE_AND_FOLDER_SPACE_MESSAGE,
  UPLOAD_FILE_SPACE_MESSAGE,
  UPLOAD_FILENAME_SPACE_MESSAGE,
  UPLOAD_FOLDER_SPACE_MESSAGE,
  UPLOAD_OVERSIZE_MESSAGE,
  UPLOAD_PHP_AND_ZIP_RESTRICTION_MESSAGE,
  UPLOAD_PHP_RESTRICTION_MESSAGE,
  UPLOAD_ZIP_CONTENT_PHP_RESTRICTION_MESSAGE,
  UPLOAD_ZIP_CONTENT_SPACE_MESSAGE,
  UPLOAD_ZIP_FILENAME_SPACE_MESSAGE,
  UPLOAD_ZIP_MANIFEST_REQUIRED_MESSAGE,
  UPLOAD_ZIP_UNSAFE_MESSAGE,
};

export const sortAlphabetically = (items = []) =>
  [...items].sort((a, b) => String(a).localeCompare(String(b)));

export const buildSoftDeletedConflictKey = (conflicts = []) =>
  conflicts.length ? sortAlphabetically(conflicts).join('\u0001') : '';

export const formatUploadElapsedTime = (timeInSeconds) => {
  const isValid = !Number.isNaN(timeInSeconds) && timeInSeconds !== 'Infinity';
  const hours = isValid ? Math.floor(timeInSeconds / 3600) : 0;
  const minutes = isValid ? Math.floor((timeInSeconds % 3600) / 60) : 0;
  const seconds = isValid ? Math.floor(timeInSeconds % 60) : 0;
  return `${String(hours).padStart(2, '0')} : ${String(minutes).padStart(2, '0')} : ${String(seconds).padStart(2, '0')}`;
};

export const normalizeUploadNameForCompare = (rawName) => {
  const text = String(rawName || '').trim();
  if (!text) {
    return '';
  }

  try {
    return decodeURIComponent(text).replace(/%20/g, ' ').replace(/%26/g, '&');
  } catch {
    return text.replace(/%20/g, ' ').replace(/%26/g, '&');
  }
};

export const checkFileAndFolderExistence = (selectedFiles = [], directoriesCsv = '') => {
  const directories = String(directoriesCsv || '')
    .split(',')
    .map((entry) => normalizeUploadNameForCompare(entry))
    .filter(Boolean);
  const foundFiles = [];
  const foundFolders = [];

  selectedFiles.forEach((file) => {
    const filePath = String(file.path || '')
      .split('/')
      .map((segment) => normalizeUploadNameForCompare(segment))
      .filter(Boolean);
    if (!filePath.length) {
      return;
    }

    if (filePath.length > 1) {
      const topLevelFolder = filePath[0];
      if (directories.includes(topLevelFolder)) {
        const folderEntry = `Directory - ${topLevelFolder}`;
        if (!foundFolders.includes(folderEntry)) {
          foundFolders.push(folderEntry);
          foundFiles.push(folderEntry);
        }
      }
      return;
    }

    if (directories.includes(filePath[0])) {
      foundFiles.push(filePath[0]);
    }
  });

  return foundFiles;
};

export const buildSuccessFileTableRows = (filesToUpload = []) =>
  filesToUpload.map((uploadFile, index) => {
    const trColor = index % 2 === 0 ? "backgroundColor:'#DDDDDD'" : '';
    return (
      <tr align="left" key={index} style={{ trColor }}>
        <td align="left" valign="middle" style={{ border: '1px solid black' }}>{uploadFile.path}</td>
        <td align="left" valign="middle" style={{ border: '1px solid black' }}>{uploadFile.size}</td>
      </tr>
    );
  });

export const sendWebSocketIfOpen = (ws, data) => {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(data);
    return true;
  }
  console.error('WebSocket is not open or is not available.');
  return false;
};

export const filterInfectedFileRows = (rows, infectedFile) =>
  rows.filter((row) => row.props['data-filename'] !== infectedFile);

export const applyUploadWebSocketMessage = (data, callbacks) => {
  if (data.topic === 'scan-result' && data.status === 'infected') {
    callbacks.onInfectedFile(data.infectedFile);
    return;
  }

  if (data.topic === 'time') {
    callbacks.onTime(data.data);
    return;
  }

  callbacks.onProgress(data.data);
};

export const createUploadWebSocketMessageHandler = (callbacks) => (event) => {
  const data = JSON.parse(event.data);
  applyUploadWebSocketMessage(data, callbacks);
};

export const readFileInChunks = ({
  file,
  chunkSize = 1024 * 1024 * 10,
  onChunkRead,
  onComplete,
}) => {
  let offset = 0;
  const reader = new FileReader();

  const readNextChunk = () => {
    reader.readAsArrayBuffer(file.slice(offset, offset + chunkSize));
  };

  reader.onload = (progressEvent) => {
    onChunkRead({
      chunk: reader.result,
      loaded: progressEvent.loaded,
      file,
    });
    offset += chunkSize;
    if (offset < file.size) {
      readNextChunk();
    } else {
      onComplete({ file });
    }
  };

  readNextChunk();
};

export const resolveFilesToUpload = ({
  conflictState,
  mode,
  acceptedFiles,
}) => {
  if (!conflictState) {
    return null;
  }

  if (mode !== 'replace' && conflictState.replaceConflicts.length > 0) {
    return { filesToUpload: [], shouldAbort: true };
  }

  const filesToUpload = getUploadableFiles({
    acceptedFiles,
    replaceConflicts: conflictState.replaceConflicts,
    softDeletedConflicts: conflictState.softDeletedConflicts,
    mode,
  });

  if (!filesToUpload.length) {
    return { filesToUpload: [], shouldAbort: true };
  }

  return { filesToUpload, shouldAbort: false };
};

export const hasUploadConflictMarkers = (conflictState) =>
  Boolean(
    conflictState &&
      (conflictState.replaceConflicts.length > 0 ||
        conflictState.softDeletedConflicts.length > 0)
  );

export const mapReplaceConflictsToExemptNames = (replaceConflicts = []) =>
  replaceConflicts
    .map((entry) => normalizeUploadNameForCompare(normalizeConflictDisplayName(entry)))
    .filter(Boolean);

export const getBatchUploadAlert = (batchValidation) => {
  if (batchValidation?.valid) {
    return null;
  }
  if (batchValidation.reason === 'oversize') {
    return {
      message: UPLOAD_OVERSIZE_MESSAGE,
      clearFilesFound: true,
    };
  }
  if (batchValidation.reason === 'forbidden') {
    return {
      message: 'You cannot upload files with these extensions',
      clearFilesFound: false,
    };
  }
  return null;
};

export const executeBulkUploadStart = ({
  filesToUpload,
  batchValidation,
  sendPath,
  sendStartMessage,
  sendEndMessage,
  sendFileInChunks,
  setUploadProgress,
  setDisplayFileSection,
  setITotal,
  setfilesfound,
}) => {
  setfilesfound(false);
  sendPath();
  setITotal(batchValidation.totalSize);

  if (batchValidation.totalSize === 0) {
    filesToUpload.forEach((file) => {
      sendStartMessage(file);
      sendEndMessage(file);
    });
    setUploadProgress('100%');
    setDisplayFileSection(true);
    return;
  }

  sendFileInChunks(filesToUpload[0], 0, filesToUpload);
  setUploadProgress('1%');
};

const SPACE_CHAR_REGEX = /\s/;

export const pathSegmentContainsSpace = (segment) =>
  typeof segment === 'string' && SPACE_CHAR_REGEX.test(segment);

export const getUploadPathSegments = (filePath) => {
  const normalized = String(filePath || '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/^(\.\/|\/)+/, '');
  return normalized.split('/').filter(Boolean);
};

export const getUploadPathBaseName = (value) => {
  const segments = getUploadPathSegments(value);
  return segments.length ? segments[segments.length - 1] : '';
};

export const isZipUploadFile = (file) => {
  const baseName = getUploadPathBaseName(file?.path || file?.name || '');
  return baseName.toLowerCase().endsWith('.zip');
};

export const toExemptTopLevelNameSet = (exemptTopLevelNames = []) => {
  if (exemptTopLevelNames instanceof Set) {
    return exemptTopLevelNames;
  }
  return new Set(
    (Array.isArray(exemptTopLevelNames) ? exemptTopLevelNames : [])
      .map((name) => normalizeUploadNameForCompare(normalizeConflictDisplayName(name)))
      .filter(Boolean)
  );
};

export const findSpaceViolationsInPaths = (filesToUpload = [], exemptTopLevelNames = []) => {
  const exemptSet = toExemptTopLevelNameSet(exemptTopLevelNames);
  const violations = [];

  filesToUpload.forEach((file) => {
    const segments = getUploadPathSegments(file?.path || file?.name);
    if (!segments.length) {
      return;
    }

    const topLevelExempt = exemptSet.has(normalizeUploadNameForCompare(segments[0]));
    const segmentsToValidate = topLevelExempt ? segments.slice(1) : segments;
    const relativeOffendingIndex = segmentsToValidate.findIndex((segment) =>
      pathSegmentContainsSpace(segment)
    );
    if (relativeOffendingIndex < 0) {
      return;
    }

    const absoluteOffendingIndex = topLevelExempt
      ? relativeOffendingIndex + 1
      : relativeOffendingIndex;
    const offendingSegment = segments[absoluteOffendingIndex];
    violations.push({
      path: file.path || file.name,
      segment: offendingSegment,
      reportPath: segments.slice(absoluteOffendingIndex).join('/'),
    });
  });

  return violations;
};

export const findSpaceViolationsInZipEntries = (entryPaths = []) => {
  const violations = [];

  entryPaths.forEach((entryPath) => {
    const segments = getUploadPathSegments(entryPath);
    const offendingSegment = segments.find((segment) => pathSegmentContainsSpace(segment));
    if (offendingSegment) {
      violations.push({ path: entryPath, segment: offendingSegment });
    }
  });

  return violations;
};

export const MAX_ZIP_ARCHIVE_BYTES = 5 * 1024 * 1024 * 1024;
export const MAX_ZIP_ENTRIES = 10_000;
export const MAX_ZIP_UNCOMPRESSED_BYTES = 5 * 1024 * 1024 * 1024;
export const MAX_ZIP_COMPRESSION_RATIO = 100;

const EOCD_SIGNATURE = 0x06054b50;
const ZIP64_EOCD_SIGNATURE = 0x06064b50;
const ZIP64_EOCD_LOCATOR_SIGNATURE = 0x07064b50;
const CDFH_SIGNATURE = 0x02014b50;
const ZIP64_EXTRA_FIELD_ID = 0x0001;
const ZIP64_SIZE_PLACEHOLDER = 0xffffffff;
const ZIP64_ENTRY_COUNT_PLACEHOLDER = 0xffff;
// EOCD (22) + max comment (65535) + ZIP64 locator (20) + typical ZIP64 EOCD (56) + slack
const ZIP_DIRECTORY_TAIL_BYTES = 22 + 0xffff + 20 + 56 + 1024;

const readUInt16LE = (view, offset) => view.getUint16(offset, true);
const readUInt32LE = (view, offset) => view.getUint32(offset, true);
const readUInt64LEAsNumber = (view, offset) => Number(view.getBigUint64(offset, true));

const decodeZipEntryName = (nameBytes) => {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder('utf-8').decode(nameBytes);
  }

  let result = '';
  for (let index = 0; index < nameBytes.length; index += 1) {
    result += String.fromCharCode(nameBytes[index]);
  }
  return result;
};

export const findEndOfCentralDirectoryOffset = (view) => {
  const maxCommentLength = 0xffff;
  const searchStart = Math.max(0, view.byteLength - 22 - maxCommentLength);

  for (let offset = view.byteLength - 22; offset >= searchStart; offset -= 1) {
    if (readUInt32LE(view, offset) === EOCD_SIGNATURE) {
      return offset;
    }
  }

  return -1;
};

/**
 * ZIP64 extra field order (APPNOTE): uncompressed size, then compressed size,
 * then relative offset / disk start — each present only when the matching CD
 * field is the 0xFFFF/0xFFFFFFFF placeholder.
 */
const readZip64ExtraFieldSizes = (
  view,
  extraFieldOffset,
  extraFieldLength,
  { needUncompressedSize = false, needCompressedSize = false } = {}
) => {
  let offset = extraFieldOffset;
  const extraFieldEnd = extraFieldOffset + extraFieldLength;
  let uncompressedSize = null;
  let compressedSize = null;

  while (offset + 4 <= extraFieldEnd) {
    const headerId = readUInt16LE(view, offset);
    const dataSize = readUInt16LE(view, offset + 2);
    const dataOffset = offset + 4;
    const dataEnd = dataOffset + dataSize;

    if (dataEnd > extraFieldEnd) {
      break;
    }

    if (headerId === ZIP64_EXTRA_FIELD_ID) {
      let zip64Offset = dataOffset;
      if (needUncompressedSize && dataEnd - zip64Offset >= 8) {
        uncompressedSize = readUInt64LEAsNumber(view, zip64Offset);
        zip64Offset += 8;
      }
      if (needCompressedSize && dataEnd - zip64Offset >= 8) {
        compressedSize = readUInt64LEAsNumber(view, zip64Offset);
      }
      break;
    }

    offset = dataEnd;
  }

  return { compressedSize, uncompressedSize };
};

const parseCentralDirectoryEntriesAt = (arrayBuffer, centralDirOffset, entryCount) => {
  if (entryCount > MAX_ZIP_ENTRIES) {
    return { entries: [], error: 'too_many_entries' };
  }

  const view = new DataView(arrayBuffer);
  const entries = [];
  let offset = centralDirOffset;

  for (let index = 0; index < entryCount; index += 1) {
    if (offset + 46 > view.byteLength || readUInt32LE(view, offset) !== CDFH_SIGNATURE) {
      throw new Error('invalid_zip');
    }

    let compressedSize = readUInt32LE(view, offset + 20);
    let uncompressedSize = readUInt32LE(view, offset + 24);
    const fileNameLength = readUInt16LE(view, offset + 28);
    const extraFieldLength = readUInt16LE(view, offset + 30);
    const fileCommentLength = readUInt16LE(view, offset + 32);
    const nameStart = offset + 46;
    const nameEnd = nameStart + fileNameLength;

    if (nameEnd > view.byteLength) {
      throw new Error('invalid_zip');
    }

    const nameBytes = new Uint8Array(arrayBuffer, nameStart, fileNameLength);
    const name = decodeZipEntryName(nameBytes);

    const needUncompressedSize = uncompressedSize === ZIP64_SIZE_PLACEHOLDER;
    const needCompressedSize = compressedSize === ZIP64_SIZE_PLACEHOLDER;
    if (needUncompressedSize || needCompressedSize) {
      const zip64Sizes = readZip64ExtraFieldSizes(view, nameEnd, extraFieldLength, {
        needUncompressedSize,
        needCompressedSize,
      });
      if (needCompressedSize && zip64Sizes.compressedSize != null) {
        compressedSize = zip64Sizes.compressedSize;
      }
      if (needUncompressedSize && zip64Sizes.uncompressedSize != null) {
        uncompressedSize = zip64Sizes.uncompressedSize;
      }
    }

    entries.push({ name, compressedSize, uncompressedSize });
    offset = nameEnd + extraFieldLength + fileCommentLength;
  }

  return { entries, error: null };
};

const readZip64EndOfCentralDirectory = (view, locatorOffsetInView) => {
  if (
    locatorOffsetInView < 0 ||
    locatorOffsetInView + 20 > view.byteLength ||
    readUInt32LE(view, locatorOffsetInView) !== ZIP64_EOCD_LOCATOR_SIGNATURE
  ) {
    return null;
  }

  const zip64EocdOffset = readUInt64LEAsNumber(view, locatorOffsetInView + 8);
  return { zip64EocdOffset };
};

const resolveCentralDirectoryLocation = (view, eocdOffsetInView, tailStartOffset, archiveSize) => {
  let entryCount = readUInt16LE(view, eocdOffsetInView + 10);
  let centralDirSize = readUInt32LE(view, eocdOffsetInView + 12);
  let centralDirOffset = readUInt32LE(view, eocdOffsetInView + 16);

  const needsZip64 =
    entryCount === ZIP64_ENTRY_COUNT_PLACEHOLDER ||
    centralDirSize === ZIP64_SIZE_PLACEHOLDER ||
    centralDirOffset === ZIP64_SIZE_PLACEHOLDER;

  if (needsZip64) {
    const locatorOffsetInView = eocdOffsetInView - 20;
    const locator = readZip64EndOfCentralDirectory(view, locatorOffsetInView);
    if (!locator) {
      throw new Error('invalid_zip');
    }

    const zip64EocdOffsetInView = locator.zip64EocdOffset - tailStartOffset;
    if (
      zip64EocdOffsetInView < 0 ||
      zip64EocdOffsetInView + 56 > view.byteLength ||
      readUInt32LE(view, zip64EocdOffsetInView) !== ZIP64_EOCD_SIGNATURE
    ) {
      throw new Error('invalid_zip');
    }

    entryCount = readUInt64LEAsNumber(view, zip64EocdOffsetInView + 32);
    centralDirSize = readUInt64LEAsNumber(view, zip64EocdOffsetInView + 40);
    centralDirOffset = readUInt64LEAsNumber(view, zip64EocdOffsetInView + 48);
  }

  if (
    !Number.isFinite(entryCount) ||
    !Number.isFinite(centralDirSize) ||
    !Number.isFinite(centralDirOffset) ||
    entryCount < 0 ||
    centralDirSize < 0 ||
    centralDirOffset < 0 ||
    centralDirOffset + centralDirSize > archiveSize
  ) {
    throw new Error('invalid_zip');
  }

  return { entryCount, centralDirSize, centralDirOffset };
};

export const parseZipCentralDirectoryEntries = (arrayBuffer) => {
  const view = new DataView(arrayBuffer);
  const eocdOffset = findEndOfCentralDirectoryOffset(view);

  if (eocdOffset < 0) {
    throw new Error('invalid_zip');
  }

  const { entryCount, centralDirOffset } = resolveCentralDirectoryLocation(
    view,
    eocdOffset,
    0,
    view.byteLength
  );

  return parseCentralDirectoryEntriesAt(arrayBuffer, centralDirOffset, entryCount);
};

export const isUnsafeZipEntryPath = (entryPath) => {
  if (typeof entryPath !== 'string' || !entryPath.trim()) {
    return true;
  }

  const normalized = entryPath.replace(/\\/g, '/');
  if (normalized.startsWith('/') || /^[a-zA-Z]:/.test(normalized)) {
    return true;
  }

  return normalized.split('/').some((segment) => segment === '..');
};

export const assessZipEntryMetadataSafety = (entries = [], archiveCompressedSize = 0) => {
  if (!entries.length) {
    return { safe: true, entryPaths: [] };
  }

  if (entries.length > MAX_ZIP_ENTRIES) {
    return { safe: false, reason: 'too_many_entries' };
  }

  let totalUncompressed = 0;
  const entryPaths = [];

  for (const entry of entries) {
    const entryPath = entry.name;
    if (isUnsafeZipEntryPath(entryPath)) {
      return { safe: false, reason: 'unsafe_path' };
    }

    entryPaths.push(entryPath);

    const uncompressedSize = entry.uncompressedSize ?? 0;
    const compressedSize = entry.compressedSize ?? 0;
    totalUncompressed += uncompressedSize;

    if (totalUncompressed > MAX_ZIP_UNCOMPRESSED_BYTES) {
      return { safe: false, reason: 'zip_bomb' };
    }

    if (
      compressedSize > 0 &&
      uncompressedSize / compressedSize > MAX_ZIP_COMPRESSION_RATIO
    ) {
      return { safe: false, reason: 'zip_bomb' };
    }
  }

  if (
    archiveCompressedSize > 0 &&
    totalUncompressed / archiveCompressedSize > MAX_ZIP_COMPRESSION_RATIO
  ) {
    return { safe: false, reason: 'zip_bomb' };
  }

  return { safe: true, entryPaths };
};

const readBlobAsArrayBuffer = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsArrayBuffer(blob);
  });

const readCentralDirectoryLocationFromFile = async (file, archiveSize) => {
  const tailSize = Math.min(archiveSize, ZIP_DIRECTORY_TAIL_BYTES);
  const tailStart = archiveSize - tailSize;
  const tailBuffer = await readBlobAsArrayBuffer(file.slice(tailStart, archiveSize));
  const tailView = new DataView(tailBuffer);
  const eocdOffsetInTail = findEndOfCentralDirectoryOffset(tailView);

  if (eocdOffsetInTail < 0) {
    throw new Error('invalid_zip');
  }

  let entryCount = readUInt16LE(tailView, eocdOffsetInTail + 10);
  let centralDirSize = readUInt32LE(tailView, eocdOffsetInTail + 12);
  let centralDirOffset = readUInt32LE(tailView, eocdOffsetInTail + 16);

  const needsZip64 =
    entryCount === ZIP64_ENTRY_COUNT_PLACEHOLDER ||
    centralDirSize === ZIP64_SIZE_PLACEHOLDER ||
    centralDirOffset === ZIP64_SIZE_PLACEHOLDER;

  if (needsZip64) {
    const locatorOffsetInTail = eocdOffsetInTail - 20;
    const locator = readZip64EndOfCentralDirectory(tailView, locatorOffsetInTail);
    if (!locator) {
      throw new Error('invalid_zip');
    }

    let zip64View = tailView;
    let zip64OffsetInView = locator.zip64EocdOffset - tailStart;

    if (
      zip64OffsetInView < 0 ||
      zip64OffsetInView + 56 > tailView.byteLength
    ) {
      const zip64Buffer = await readBlobAsArrayBuffer(
        file.slice(locator.zip64EocdOffset, locator.zip64EocdOffset + 56)
      );
      zip64View = new DataView(zip64Buffer);
      zip64OffsetInView = 0;
    }

    if (readUInt32LE(zip64View, zip64OffsetInView) !== ZIP64_EOCD_SIGNATURE) {
      throw new Error('invalid_zip');
    }

    entryCount = readUInt64LEAsNumber(zip64View, zip64OffsetInView + 32);
    centralDirSize = readUInt64LEAsNumber(zip64View, zip64OffsetInView + 40);
    centralDirOffset = readUInt64LEAsNumber(zip64View, zip64OffsetInView + 48);
  }

  if (
    !Number.isFinite(entryCount) ||
    !Number.isFinite(centralDirSize) ||
    !Number.isFinite(centralDirOffset) ||
    entryCount < 0 ||
    centralDirSize < 0 ||
    centralDirOffset < 0 ||
    centralDirOffset + centralDirSize > archiveSize
  ) {
    throw new Error('invalid_zip');
  }

  return { entryCount, centralDirSize, centralDirOffset };
};

/**
 * Reads ZIP entry metadata from the central directory only (EOCD/CD slices),
 * without loading the full archive into memory. Entry names are still returned
 * so callers can enforce filename/folder space rules.
 */
export const loadZipArchiveSafely = async (file) => {
  const archiveSize = file?.size ?? 0;
  if (archiveSize > MAX_ZIP_ARCHIVE_BYTES) {
    return { ok: false, reason: 'archive_too_large' };
  }

  if (archiveSize < 22) {
    return { ok: false, reason: 'invalid_archive' };
  }

  try {
    const { entryCount, centralDirSize, centralDirOffset } =
      await readCentralDirectoryLocationFromFile(file, archiveSize);

    const directoryBuffer = await readBlobAsArrayBuffer(
      file.slice(centralDirOffset, centralDirOffset + centralDirSize)
    );
    const parsed = parseCentralDirectoryEntriesAt(directoryBuffer, 0, entryCount);

    if (parsed.error) {
      return { ok: false, reason: parsed.error };
    }

    const safety = assessZipEntryMetadataSafety(parsed.entries, archiveSize);
    if (!safety.safe) {
      return { ok: false, reason: safety.reason };
    }

    return { ok: true, entryPaths: safety.entryPaths };
  } catch {
    return { ok: false, reason: 'invalid_archive' };
  }
};

const ZIP_SIZE_FAILURE_REASONS = new Set(['archive_too_large', 'zip_bomb']);

export const isZipSizeFailureReason = (reason) => ZIP_SIZE_FAILURE_REASONS.has(reason);

export const getZipLoadFailureResult = (loadResult = {}) => {
  if (isZipSizeFailureReason(loadResult.reason)) {
    return {
      valid: false,
      reason: 'oversize',
      message: UPLOAD_OVERSIZE_MESSAGE,
    };
  }

  return {
    valid: false,
    reason: 'zip_unsafe',
    message: UPLOAD_ZIP_UNSAFE_MESSAGE,
  };
};

export const validateZipFileForSpaces = async (file, exemptTopLevelNames = []) => {
  const exemptSet = toExemptTopLevelNameSet(exemptTopLevelNames);
  const segments = getUploadPathSegments(file?.path);
  const topLevel = segments[0] || getUploadPathBaseName(file?.name);
  const topLevelExempt =
    topLevel && exemptSet.has(normalizeUploadNameForCompare(topLevel));

  const zipFileName = getUploadPathBaseName(file?.path || file?.name);
  if (!topLevelExempt && pathSegmentContainsSpace(zipFileName)) {
    return {
      valid: false,
      reason: 'zip_filename_spaces',
      message: UPLOAD_ZIP_FILENAME_SPACE_MESSAGE,
    };
  }

  const loadResult = await loadZipArchiveSafely(file);
  if (!loadResult.ok) {
    return getZipLoadFailureResult(loadResult);
  }

  // Replace of an existing FSx ZIP: allow legacy spaced names inside the archive.
  if (topLevelExempt) {
    return { valid: true };
  }

  const violations = findSpaceViolationsInZipEntries(loadResult.entryPaths);
  if (violations.length) {
    return {
      valid: false,
      reason: 'zip_content_spaces',
      message: UPLOAD_ZIP_CONTENT_SPACE_MESSAGE,
    };
  }

  return { valid: true };
};

export const getUploadFilePathKey = (file) => file?.path || file?.name || '';

export const classifyUploadSpaceViolation = (name) => {
  const segments = getUploadPathSegments(name);
  if (!segments.length) {
    return 'file';
  }

  const lastSegment = segments[segments.length - 1];
  const spacedSegmentIndex = segments.findIndex((segment) => pathSegmentContainsSpace(segment));

  if (segments.length === 1 && lastSegment.toLowerCase().endsWith('.zip')) {
    return 'file';
  }

  if (spacedSegmentIndex >= 0 && spacedSegmentIndex < segments.length - 1) {
    return 'directory';
  }

  if (segments.length === 1 && /\.[^./\\]+$/.test(lastSegment)) {
    return 'file';
  }

  if (segments.length > 1 && spacedSegmentIndex === segments.length - 1) {
    return 'file';
  }

  if (segments.length === 1) {
    return 'directory';
  }

  return 'file';
};

export const getUploadSpaceViolationDisplayName = (name, type) => {
  const segments = getUploadPathSegments(name);
  if (type !== 'directory' || !segments.length) {
    return String(name || '').trim();
  }

  const spacedSegmentIndex = segments.findIndex((segment) => pathSegmentContainsSpace(segment));
  if (spacedSegmentIndex >= 0) {
    return segments.slice(0, spacedSegmentIndex + 1).join('/');
  }

  return String(name || '').trim();
};

export const partitionUploadSpaceViolations = (violatingNames = []) => {
  const directories = [];
  const files = [];
  const seenDirectories = new Set();
  const seenFiles = new Set();

  violatingNames.forEach((name) => {
    const type = classifyUploadSpaceViolation(name);
    const displayName = getUploadSpaceViolationDisplayName(name, type);

    if (type === 'directory') {
      if (!seenDirectories.has(displayName)) {
        seenDirectories.add(displayName);
        directories.push(displayName);
      }
      return;
    }

    if (!seenFiles.has(displayName)) {
      seenFiles.add(displayName);
      files.push(displayName);
    }
  });

  return { directories, files };
};

export const getUploadSpaceViolationMessage = ({
  violatingDirectories = [],
  violatingFiles = [],
  reason = 'spaces',
} = {}) => {
  if (reason === 'oversize') {
    return UPLOAD_OVERSIZE_MESSAGE;
  }
  if (reason === 'zip_unsafe') {
    return UPLOAD_ZIP_UNSAFE_MESSAGE;
  }
  if (reason === 'zip_filename_spaces') {
    return UPLOAD_ZIP_FILENAME_SPACE_MESSAGE;
  }
  if (reason === 'zip_content_spaces') {
    return UPLOAD_ZIP_CONTENT_SPACE_MESSAGE;
  }

  const hasDirectories = violatingDirectories.length > 0;
  const hasFiles = violatingFiles.length > 0;

  if (hasDirectories && hasFiles) {
    return UPLOAD_FILE_AND_FOLDER_SPACE_MESSAGE;
  }
  if (hasDirectories) {
    return UPLOAD_FOLDER_SPACE_MESSAGE;
  }
  if (hasFiles) {
    return UPLOAD_FILE_SPACE_MESSAGE;
  }

  return UPLOAD_FILE_SPACE_MESSAGE;
};

export const collectUploadSpaceViolations = async (
  filesToUpload = [],
  exemptTopLevelNames = []
) => {
  const exemptSet = toExemptTopLevelNameSet(exemptTopLevelNames);
  const violatingNames = [];
  const seenNames = new Set();

  const addViolatingName = (name) => {
    const key = String(name || '').trim();
    if (!key || seenNames.has(key)) {
      return;
    }
    seenNames.add(key);
    violatingNames.push(key);
  };

  let reason = null;

  const nonZipFiles = filesToUpload.filter((file) => !isZipUploadFile(file));
  const pathViolations = findSpaceViolationsInPaths(nonZipFiles, exemptTopLevelNames);
  if (pathViolations.length) {
    reason = 'spaces';
    pathViolations.forEach((violation) => {
      addViolatingName(violation.reportPath || violation.path || violation.segment);
    });
  }

  const zipFiles = filesToUpload.filter(isZipUploadFile);
  for (const zipFile of zipFiles) {
    const segments = getUploadPathSegments(zipFile?.path);
    const topLevel = segments[0] || getUploadPathBaseName(zipFile?.name);
    const topLevelExempt =
      topLevel && exemptSet.has(normalizeUploadNameForCompare(topLevel));

    const zipPath = getUploadFilePathKey(zipFile);
    const zipFileName = getUploadPathBaseName(zipFile?.path || zipFile?.name);

    if (!topLevelExempt && pathSegmentContainsSpace(zipFileName)) {
      reason = reason || 'zip_filename_spaces';
      addViolatingName(zipPath);
      continue;
    }

    const loadResult = await loadZipArchiveSafely(zipFile);
    if (!loadResult.ok) {
      const failure = getZipLoadFailureResult(loadResult);
      return {
        ...failure,
        violatingNames: [],
        violatingDirectories: [],
        violatingFiles: [],
      };
    }

    // Replace of an existing FSx ZIP: allow legacy spaced names inside the archive.
    if (topLevelExempt) {
      continue;
    }

    const zipEntryViolations = findSpaceViolationsInZipEntries(loadResult.entryPaths);
    if (zipEntryViolations.length) {
      reason = reason || 'zip_content_spaces';
      zipEntryViolations.forEach((violation) => {
        addViolatingName(violation.path || violation.segment);
      });
    }
  }

  if (violatingNames.length) {
    const { directories, files } = partitionUploadSpaceViolations(violatingNames);
    return {
      valid: false,
      reason,
      message: getUploadSpaceViolationMessage({
        violatingDirectories: directories,
        violatingFiles: files,
        reason,
      }),
      violatingNames,
      violatingDirectories: directories,
      violatingFiles: files,
    };
  }

  return {
    valid: true,
    violatingNames: [],
    violatingDirectories: [],
    violatingFiles: [],
  };
};

export const removeSpaceViolatingUploadFiles = async (
  files = [],
  exemptTopLevelNames = []
) => {
  const violatingPathKeys = new Set(
    findSpaceViolationsInPaths(
      files.filter((file) => !isZipUploadFile(file)),
      exemptTopLevelNames
    ).map((violation) => violation.path || '')
  );

  const zipFilesToRemove = new Set();
  const zipFiles = files.filter(isZipUploadFile);
  for (const zipFile of zipFiles) {
    const zipValidation = await validateZipFileForSpaces(zipFile, exemptTopLevelNames);
    if (!zipValidation.valid) {
      zipFilesToRemove.add(getUploadFilePathKey(zipFile));
    }
  }

  return files.filter((file) => {
    const pathKey = getUploadFilePathKey(file);
    if (isZipUploadFile(file)) {
      return !zipFilesToRemove.has(pathKey);
    }
    return !violatingPathKeys.has(pathKey);
  });
};

export const validateUploadFilenameSpaces = async (
  filesToUpload = [],
  exemptTopLevelNames = []
) => {
  const result = await collectUploadSpaceViolations(filesToUpload, exemptTopLevelNames);
  if (result.valid) {
    return { valid: true };
  }

  return {
    valid: false,
    reason: result.reason,
    message: result.message,
  };
};

export const validateUploadBatch = (filesToUpload, forbiddenExtensions = []) => {
  const totalSize = filesToUpload.reduce((sum, file) => sum + file.size, 0);
  if (totalSize > 5 * 1024 * 1024 * 1024) {
    return { valid: false, reason: 'oversize' };
  }

  const hasForbiddenExtensions = filesToUpload.some((file) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    return forbiddenExtensions.includes(fileExtension);
  });

  if (hasForbiddenExtensions) {
    return { valid: false, reason: 'forbidden' };
  }

  return { valid: true, totalSize };
};

const SLASH_CHAR_CODE = 47;

/** Trim leading/trailing slashes without regex (avoids ReDoS on hostile input). */
const trimSlashes = (value) => {
  const text = String(value || '');
  let start = 0;
  let end = text.length;

  while (start < end && text.charCodeAt(start) === SLASH_CHAR_CODE) {
    start += 1;
  }
  while (end > start && text.charCodeAt(end - 1) === SLASH_CHAR_CODE) {
    end -= 1;
  }

  return start === 0 && end === text.length ? text : text.slice(start, end);
};

export const isPhpUploadFileName = (fileName) => {
  const baseName = getUploadPathBaseName(fileName);
  if (!baseName || !baseName.includes('.')) {
    return false;
  }
  const extension = baseName.split('.').pop().toLowerCase();
  return PHP_DEPRECATION_EXTENSIONS.includes(extension);
};

export const isUnderPhp8DesignatedPath = (relativePath) => {
  const normalized = trimSlashes(String(relativePath || '').replace(/\\/g, '/')).toLowerCase();
  if (!normalized) {
    return false;
  }

  return PHP8_DESIGNATED_PATH_PREFIXES.some((prefix) => {
    const normalizedPrefix = trimSlashes(String(prefix).toLowerCase());
    if (!normalizedPrefix) {
      return false;
    }
    return (
      normalized === normalizedPrefix
      || normalized.startsWith(`${normalizedPrefix}/`)
      || normalized.includes(`/${normalizedPrefix}/`)
      || normalized.endsWith(`/${normalizedPrefix}`)
    );
  });
};

export const isZipInnerPhpRestrictedPath = (path) => String(path || '').includes(' → ');

export const getPhpRestrictionViolationMessage = (restrictedPaths = []) => {
  const paths = Array.isArray(restrictedPaths) ? restrictedPaths : [];
  const hasZipInner = paths.some(isZipInnerPhpRestrictedPath);
  const hasLoose = paths.some((entry) => !isZipInnerPhpRestrictedPath(entry));

  if (hasZipInner && hasLoose) {
    return UPLOAD_PHP_AND_ZIP_RESTRICTION_MESSAGE;
  }
  if (hasZipInner) {
    return UPLOAD_ZIP_CONTENT_PHP_RESTRICTION_MESSAGE;
  }
  return UPLOAD_PHP_RESTRICTION_MESSAGE;
};

export const partitionPhpRestrictedPaths = (restrictedPaths = []) => ({
  directories: [],
  files: (Array.isArray(restrictedPaths) ? restrictedPaths : [])
    .map((path) => String(path || '').trim())
    .filter(Boolean),
});

export const buildZipContentsFromUploadFiles = async (files = []) => {
  const zipContents = {};
  const zipFiles = files.filter(isZipUploadFile);

  for (const zipFile of zipFiles) {
    const zipPath = getUploadFilePathKey(zipFile);
    const loadResult = await loadZipArchiveSafely(zipFile);
    if (!loadResult.ok) {
      zipContents[zipPath] = [];
      continue;
    }

    zipContents[zipPath] = loadResult.entryPaths.filter((entry) => entry && !String(entry).endsWith('/'));
  }

  return zipContents;
};
